import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Modal } from '../../components/ui/modal'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import ExamTimer from '../../components/exam/ExamTimer'
import ObjectiveQuestion from '../../components/exam/ObjectiveQuestion'
import TheoryQuestion from '../../components/exam/TheoryQuestion'
import FillInGapsQuestion from '../../components/exam/FillInGapsQuestion'
import { cbtService } from '../../services/cbtService'
import { useAuth } from '../../context/AuthContext'

const CBTExam = () => {
  const { examId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // State management
  const [exam, setExam] = useState(null)
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [examStarted, setExamStarted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [violations, setViolations] = useState(0)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  
  // Refs
  const autosaveIntervalRef = useRef(null)
  const heartbeatIntervalRef = useRef(null)
  const fullscreenCheckIntervalRef = useRef(null)
  const reportedViolationsRef = useRef(new Set()) // Track which violations have been reported

  useEffect(() => {
    initializeExam()
    setupEventListeners()
    
    return () => {
      cleanup()
    }
  }, [examId])

  useEffect(() => {
    if (examStarted) {
      startAutosave()
      startHeartbeat()
      startFullscreenMonitoring()
    }
    
    return () => {
      stopAutosave()
      stopHeartbeat()
      stopFullscreenMonitoring()
    }
  }, [examStarted, answers])

  useEffect(() => {
    if (timeRemaining <= 0 && examStarted) {
      handleAutoSubmit()
    }
  }, [timeRemaining, examStarted])

  // Countdown timer - decrement every second when exam is started
  useEffect(() => {
    if (!examStarted) return
    
    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev > 0) return prev - 1
        return 0
      })
    }, 1000)
    
    return () => clearInterval(timerInterval)
  }, [examStarted])

  const initializeExam = async () => {
    try {
      setLoading(true)
      
      // Start the exam session
      await cbtService.startExam(examId)
      
      // Get exam details and questions
      const response = await cbtService.getExamDetails(examId)
      const examData = response.data.data
      
      setExam(examData.exam)
      setQuestions(examData.questions)
      setTimeRemaining(examData.timeRemaining)
      setExamStarted(true)
      
      // Load any existing answers
      if (examData.existingAnswers) {
        setAnswers(examData.existingAnswers)
      }
      
      // Request fullscreen
      requestFullscreen()
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start exam')
    } finally {
      setLoading(false)
    }
  }

  const setupEventListeners = () => {
    // Online/offline detection
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    // Prevent context menu
    const handleContextMenu = (e) => e.preventDefault()
    document.addEventListener('contextmenu', handleContextMenu)
    
    // Prevent keyboard shortcuts
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault()
        // Log violation only once per session
        if (!reportedViolationsRef.current.has('keyboard_shortcut')) {
          reportedViolationsRef.current.add('keyboard_shortcut')
          logViolation('keyboard_shortcut', { key: e.key })
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    
    // Detect tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Log violation only once per session
        if (!reportedViolationsRef.current.has('tab_switch')) {
          reportedViolationsRef.current.add('tab_switch')
          logViolation('tab_switch', { timestamp: new Date().toISOString() })
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Detect fullscreen exit
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
      
      if (!isCurrentlyFullscreen && examStarted) {
        // Log violation only once per session
        if (!reportedViolationsRef.current.has('fullscreen_exit')) {
          reportedViolationsRef.current.add('fullscreen_exit')
          logViolation('fullscreen_exit', { timestamp: new Date().toISOString() })
        }
      }
    }
    
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }

  const requestFullscreen = async () => {
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen()
      } else if (document.documentElement.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen()
      } else if (document.documentElement.mozRequestFullScreen) {
        await document.documentElement.mozRequestFullScreen()
      } else if (document.documentElement.msRequestFullscreen) {
        await document.documentElement.msRequestFullscreen()
      }
    } catch (err) {
      console.warn('Fullscreen request failed:', err)
    }
  }

  const startAutosave = () => {
    autosaveIntervalRef.current = setInterval(async () => {
      await saveProgress()
    }, 30000) // Autosave every 30 seconds
  }

  const stopAutosave = () => {
    if (autosaveIntervalRef.current) {
      clearInterval(autosaveIntervalRef.current)
      autosaveIntervalRef.current = null
    }
  }

  const startHeartbeat = () => {
    heartbeatIntervalRef.current = setInterval(async () => {
      try {
        await cbtService.sendHeartbeat(examId)
      } catch (err) {
        console.warn('Heartbeat failed:', err)
      }
    }, 10000) // Heartbeat every 10 seconds
  }

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
  }

  const startFullscreenMonitoring = () => {
    fullscreenCheckIntervalRef.current = setInterval(() => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
      
      if (!isCurrentlyFullscreen && examStarted) {
        // Log violation only once per session
        if (!reportedViolationsRef.current.has('fullscreen_exit')) {
          reportedViolationsRef.current.add('fullscreen_exit')
          logViolation('fullscreen_exit', { timestamp: new Date().toISOString() })
        }
      }
    }, 5000) // Check every 5 seconds instead of 1 second
  }

  const stopFullscreenMonitoring = () => {
    if (fullscreenCheckIntervalRef.current) {
      clearInterval(fullscreenCheckIntervalRef.current)
      fullscreenCheckIntervalRef.current = null
    }
  }

  const saveProgress = async () => {
    if (saving) return
    
    try {
      setSaving(true)
      await cbtService.autoSaveAnswers(examId, answers, timeRemaining)
    } catch (err) {
      console.warn('Autosave failed:', err)
      // Store in localStorage as backup
      localStorage.setItem(`cbt_exam_${examId}_backup`, JSON.stringify({
        answers,
        timeRemaining,
        timestamp: new Date().toISOString()
      }))
    } finally {
      setSaving(false)
    }
  }

  const logViolation = async (type, details) => {
    try {
      await cbtService.logViolation(examId, type, details)
      setViolations(prev => prev + 1)
    } catch (err) {
      console.warn('Failed to log violation:', err)
    }
  }

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  const navigateToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index)
    }
  }

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      await cbtService.submitExam(examId, answers, false)
      navigate('/cbt/success')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit exam')
    } finally {
      setLoading(false)
    }
  }

  const handleAutoSubmit = async () => {
    try {
      await cbtService.submitExam(examId, answers, true)
      navigate('/cbt/success')
    } catch (err) {
      console.error('Auto-submit failed:', err)
      // Store in localStorage as backup
      localStorage.setItem(`cbt_exam_${examId}_final_submission`, JSON.stringify({
        answers,
        timestamp: new Date().toISOString(),
        isAutoSubmit: true
      }))
      navigate('/cbt/success')
    }
  }

  const cleanup = () => {
    stopAutosave()
    stopHeartbeat()
    stopFullscreenMonitoring()
  }

  const getQuestionStatus = (index) => {
    const question = questions[index]
    if (!question) return 'unanswered'
    
    const answer = answers[question.id]
    if (!answer) return 'unanswered'
    
    // Handle different answer types
    if (typeof answer === 'string') {
      return answer.trim() !== '' ? 'answered' : 'unanswered'
    } else if (typeof answer === 'object') {
      // For fill-in-gaps questions, check if any blank has a value
      return Object.keys(answer).some(key => answer[key] && answer[key].trim && answer[key].trim() !== '') ? 'answered' : 'unanswered'
    }
    
    return 'unanswered'
  }

  const renderQuestion = () => {
    const question = questions[currentQuestionIndex]
    if (!question) return null

    const commonProps = {
      question,
      answer: answers[question.id] || '',
      onAnswerChange: (answer) => handleAnswerChange(question.id, answer),
      disabled: !examStarted
    }

    switch (question.type) {
      case 'objectives':
      case 'objective':
        return <ObjectiveQuestion {...commonProps} />
      case 'theory':
        return <TheoryQuestion {...commonProps} />
      case 'fill-in-gaps':
      case 'fill_in_gaps':
        return <FillInGapsQuestion {...commonProps} />
      default:
        return <div>Unknown question type: {question.type}</div>
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <ErrorDisplay message={error} />
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/cbt')}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Return to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <h1 className="text-xl font-semibold text-gray-900">{exam?.title}</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Student: {user?.firstName} {user?.lastName}</span>
              {violations > 0 && (
                <span className="flex items-center space-x-1 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{violations} violations</span>
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Status Indicators */}
            <div className="flex items-center space-x-3">
              {saving ? (
                <div className="flex items-center space-x-1 text-yellow-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                  <span className="text-sm">Saving...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Saved</span>
                </div>
              )}
              <div className={`w-2 h-2 rounded-full ${
                isOnline ? 'bg-green-500' : 'bg-red-500'
              }`} title={isOnline ? 'Online' : 'Offline'}></div>
            </div>
            
            {/* Timer */}
            <ExamTimer
              timeRemaining={timeRemaining}
              onTimeUp={handleAutoSubmit}
              showWarnings={true}
            />
            
            {/* Submit Button */}
            <Button
              variant="outline"
              onClick={() => setShowSubmitModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              Submit Exam
            </Button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Question Navigator Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-4">Questions</h3>
          <div className="grid grid-cols-5 gap-2 mb-4">
            {questions.map((_, index) => (
              <button
                key={index}
                onClick={() => navigateToQuestion(index)}
                className={`w-10 h-10 rounded border text-sm font-medium ${
                  index === currentQuestionIndex
                    ? 'bg-blue-600 text-white border-blue-600'
                    : getQuestionStatus(index) === 'answered'
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : 'bg-gray-100 text-gray-600 border-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
              <span>Not answered</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Question Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </h2>
              </div>
              
              {/* Render Question */}
              {renderQuestion()}
              
              {/* Navigation Buttons - Under Question */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center space-x-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleNext}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Submit Confirmation Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        title="Submit Exam"
      >
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Are you sure you want to submit your exam? This action cannot be undone.
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2 text-yellow-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Please review:</span>
            </div>
            <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
              <li>You have answered {Object.keys(answers).filter(key => {
                const answer = answers[key]
                if (!answer) return false
                if (typeof answer === 'string') {
                  return answer.trim() !== ''
                } else if (typeof answer === 'object') {
                  // For fill-in-gaps, check if any blank has a value
                  return Object.values(answer).some(val => val && val.trim && val.trim() !== '')
                }
                return false
              }).length} out of {questions.length} questions</li>
              <li>Time remaining: {Math.floor(timeRemaining / 60)} minutes {timeRemaining % 60} seconds</li>
            </ul>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowSubmitModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Submit Exam
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default CBTExam
